import { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../stores/store';
import ServicesSubsection, {
  SubSectionData,
} from '../../features/services/services-subsection/services-subsection.component';
import PersonalDataEditForm from './personal-data-form.component';
import ContactDetailsEditForm from './contact-details-form.component';
import MyAddressMultiple from './my-address-multiple/my-address-multiple.component';
import { PersonalDataContainer } from './personal-data.styles';
import LocationSettingsForm from './location-settings-form.component';
import { Measure } from '../../utils/parcel-measurements.ts/parcel-measurements-utils';
import { useLoadPlacesLibs } from '../../hooks/use-load-places-libs.hook';
import UnverifiedPill from './unverified-pill/unverified-pill.component';

const PersonalData = () => {
  const {
    userStore: { user },
    localizationsStore: { availableLocalizations },
  } = useStore();
  useLoadPlacesLibs();

  const { t } = useTranslation();

  const personalDataSectionData: SubSectionData[] = useMemo(
    () => [
      {
        label: t('first_name'),
        value: user?.first_name,
      },
      {
        label: t('middle_name'),
        value: user?.middle_name,
      },
      {
        label: t('last_name'),
        value: user?.last_name,
      },
    ],
    [user, t]
  );

  const contactDetailsSectionData: SubSectionData[] = useMemo(() => {
    let phoneNumber: string | JSX.Element = '';

    if (!user?.phone) {
      phoneNumber = <UnverifiedPill />;
    } else if (user.phone?.phone_number) {
      phoneNumber = `+${user.phone.phone_number}`;
    }

    return [
      {
        label: t('email'),
        value: user?.email,
      },
      {
        label: t('phone_number'),
        value: phoneNumber,
      },
    ];
  }, [user?.email, user?.phone, t]);

  const locationSettingsData: SubSectionData[] = useMemo(() => {
    const chosenLanguage = user?.lang
      ? availableLocalizations.find((i) => i.code === user.lang)?.languageLabel
      : 'English';

    const chosenCurrency = user?.currency || 'USD';

    const chosenMeasures =
      user?.measures === Measure.KG_CM ? t('kg_/_cm') : t('lb_/_in');

    return [
      {
        label: t('language'),
        value: chosenLanguage,
      },
      {
        label: t('currency'),
        value: chosenCurrency,
      },
      {
        label: t('measures'),
        value: chosenMeasures,
      },
    ];
  }, [user, availableLocalizations, t]);

  if (!user) return null;

  return (
    <PersonalDataContainer>
      <ServicesSubsection
        title={t('user_data')}
        data={personalDataSectionData}
        EditForm={PersonalDataEditForm}
      />
      <ServicesSubsection
        title={t('contact_details')}
        data={contactDetailsSectionData}
        EditForm={ContactDetailsEditForm}
        allowEdit
      />
      <ServicesSubsection
        title={t('location_settings')}
        data={locationSettingsData}
        EditForm={LocationSettingsForm}
      />
      <MyAddressMultiple />
    </PersonalDataContainer>
  );
};

export default observer(PersonalData);
